import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {baseApiUrl, getDynamicData} from "../service/base-url";
import Pagination from "../components/Pagination";
import axios from "axios";
import toast from "react-hot-toast";

/* =========================
   Helpers
========================= */

function isDirectVideo(url = "") {
  const u = (url || "").toLowerCase();
  return u.endsWith(".mp4") || u.endsWith(".webm") || u.endsWith(".ogg");
}

function getVideoUrl(video) {
  return (
    video?.url ||
    video?.video ||
    video?.link ||
    video?.src ||
    video?.file ||
    video?.path ||
    ""
  );
}

function getVideoTitle(video, idx) {
  return video?.title || video?.name || video?.label || `Video ${idx + 1}`;
}

function getYoutubeId(url = "") {
  if (!url) return "";
  if (url.includes("youtu.be/"))
    return url.split("youtu.be/")[1]?.split(/[?&]/)[0] || "";
  if (url.includes("watch?v="))
    return url.split("watch?v=")[1]?.split(/[?&]/)[0] || "";
  if (url.includes("/embed/"))
    return url.split("/embed/")[1]?.split(/[?&]/)[0] || "";
  return "";
}

function getVimeoId(url = "") {
  if (!url) return "";
  const parts = url.split("/");
  const last = parts[parts.length - 1]?.split(/[?&]/)[0] || "";
  return /^\d+$/.test(last) ? last : "";
}

function getEmbedUrl(url = "") {
  if (!url) return "";
  if (isDirectVideo(url)) return url;

  if (url.includes("youtube.com") || url.includes("youtu.be")) {
    const id = getYoutubeId(url);
    if (id) return `https://www.youtube.com/embed/${id}`;
  }

  if (url.includes("vimeo.com")) {
    const id = getVimeoId(url);
    if (id) return `https://player.vimeo.com/video/${id}`;
    if (url.includes("player.vimeo.com/video/")) return url;
  }

  return url;
}

function getCoverUrl(video) {
  const apiThumb =
    video?.thumbnail ||
    video?.cover ||
    video?.image ||
    video?.preview ||
    video?.poster ||
    video?.thumb ||
    "";
  if (apiThumb) return apiThumb;

  const rawUrl = getVideoUrl(video);
  const ytId = getYoutubeId(rawUrl);
  if (ytId) return `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;

  // vimeo needs API to get cover -> fallback placeholder
  return "/images/video-placeholder.jpg";
}

/* =========================
   Modal
========================= */

const VideoModal = ({ open, onClose, video }) => {
  if (!open || !video) return null;

  const rawUrl = getVideoUrl(video);
  const direct = isDirectVideo(rawUrl);
  const embedUrl = getEmbedUrl(rawUrl);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.65)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(1100px, 96vw)",
          background: "#fff",
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 14px",
            borderBottom: "1px solid #eee",
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            {video?.title || video?.name || "Video"}
          </div>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 22,
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ position: "relative", width: "100%", paddingTop: "56.25%" }}>
          <div style={{ position: "absolute", inset: 0 }}>
            {direct ? (
              <video
                src={rawUrl}
                controls
                autoPlay
                style={{ width: "100%", height: "100%", background: "#000" }}
              />
            ) : (
              <iframe
                src={embedUrl}
                title="Video player"
                allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                allowFullScreen
                style={{ width: "100%", height: "100%", border: 0, background: "#000" }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* =========================
   Page
========================= */

const About = () => {
  const [videos, setVideos] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
    const [links, setLinks] = useState(null)

  const language = useSelector((state) => state.language.language);

  useEffect(() => {
    getDynamicData("videos", (data) => setVideos(Array.isArray(data) ? data : []),
        setLinks, language);
  }, [language]);

  const handleOpen = (video) => {
    setActiveVideo(video);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setActiveVideo(null);
  };

    const fetchProduct = async (page=1) => {
        try {
            const response = await axios.get(`${baseApiUrl}/videos?page=${page}`, {
                headers: { 'Content-Type': 'application/json','x-localization': language }

            })
            const dataRes = response.data
            if (dataRes) {
                setVideos(dataRes.data)
                setLinks(dataRes.links)
            }
        } catch (error) {
            toast.error('Please try again.')
            console.error('Error:', error)
        }
    }

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: 12,
        }}
      >
        {videos.map((video, idx) => {
          const rawUrl = getVideoUrl(video);
          const title = getVideoTitle(video, idx);
          const direct = isDirectVideo(rawUrl);

          // cover for youtube/vimeo or missing mp4 poster
          const cover = getCoverUrl(video);

          return (
            <button
              key={video?.id ?? idx}
              onClick={() => handleOpen(video)}
              style={{
                border: "1px solid #eee",
                borderRadius: 12,
                overflow: "hidden",
                background: "#fff",
                cursor: "pointer",
                padding: 0,
                textAlign: "left",
              }}
            >
              {/* preview area */}
              <div style={{ position: "relative", width: "100%", paddingTop: "56.25%" }}>
                <div style={{ position: "absolute", inset: 0, background: "#000" }}>
                  {direct ? (
                    <video
                      src={rawUrl}
                      muted
                      playsInline
                      preload="metadata"
                      // poster={cover} // if you have poster
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    // For youtube/vimeo previews: use cover image (faster & reliable)
                    <img
                      src={cover}
                      alt={title}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  )}

                  {/* play icon overlay */}
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      pointerEvents: "none",
                    }}
                  >
                    <div
                      style={{
                        width: 46,
                        height: 46,
                        borderRadius: 999,
                        background: "rgba(0,0,0,0.55)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <div
                        style={{
                          width: 0,
                          height: 0,
                          borderTop: "8px solid transparent",
                          borderBottom: "8px solid transparent",
                          borderLeft: "14px solid #fff",
                          marginLeft: 2,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* title */}
              <div style={{ padding: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#111" }}>{title}</div>
              </div>
            </button>
          );
        })}
      </div>
        {links && (
            <div
                className="flex justify-center items-center py-2 cursor-pointer"
            >
                <Pagination fetchData={fetchProduct} links={links} />


            </div>
        )}

      <VideoModal open={open} onClose={handleClose} video={activeVideo} />
    </div>
  );
};

export default About;
